import type { IncomingMessage } from 'http';
import type { ParsedUrlQuery } from 'querystring';
import { camelizeKeys } from 'humps';
import { toHref, toApiLink, toExtLinkEnv, ApiEnvs } from '@peloton/external-links/models';
// eslint-disable-next-line no-restricted-imports
import { toApiEnv } from '@engage/env/api';

export type DeserializedScheduleInfo = {
  homePelotonId: string;
  rideId: string;
  scheduledStartTime: number;
  studioPelotonId?: string;
  type: string;
};

export type InviteShareResult = {
  hostUsername?: string;
  inviteId: string;
  translatedOccasionName?: string;
  scheduledClassId: string;
  createdTimezone?: string;
};

export type RideDetailsResponse = {
  ride: {
    title: string;
    image_url: string | null;
    description: string;
  };
};

export type InviteDetailsResponse = {
  rideDetails: RideDetailsResponse['ride'];
  deserializedScheduleInfo: DeserializedScheduleInfo;
  inviteShareRes: InviteShareResult;
};

const getApiOverrideFrom = (query: ParsedUrlQuery) => (): string | null => {
  const value = query?.['api-env'];

  if (value === undefined) return null;
  return Array.isArray(value) ? value[0] : value;
};

export const toApiEnvFromEvent = (request: IncomingMessage, query: ParsedUrlQuery) => {
  return toApiEnv(request.headers.host!, getApiOverrideFrom(query));
};

export const setApiEnvQueryParameter = (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  onURL: URL,
): void => {
  const host = request.headers?.host!;
  const defaultApiEnv = toApiEnv(host, undefined);
  const apiEnv = toApiEnvFromEvent(request, query);
  if (defaultApiEnv !== apiEnv) {
    onURL.searchParams.set('api-env', apiEnv);
  }
};

export const toApiBaseUrl = (request: IncomingMessage, query: ParsedUrlQuery) => {
  const api = toApiEnvFromEvent(request, query);
  return toHref(toApiLink('/'), toExtLinkEnv({ api }));
};

const getFetchOptions = (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  optBaseUrlOverride?: string,
) => {
  const baseURL = optBaseUrlOverride || toApiBaseUrl(request, query);
  const headers = {
    'Peloton-Platform': 'web',
    'User-Agent': 'peloton/share-proxy',
  };
  return { baseURL, headers };
};

const getInviteFriendsBaseUrl = (request: IncomingMessage) =>
  toApiEnv(request.headers?.host!, undefined) === ApiEnvs.Prod
    ? 'https://invite-friends-service.prod.k8s.onepeloton.com'
    : 'https://invite-friends-service.stage.k8s.onepeloton.com';

const fetchRideDetails = async (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  classId: string,
) => {
  const { baseURL, headers } = getFetchOptions(request, query);
  const url = new URL(`/api/public/ride/${classId}`, baseURL).toString();

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data: RideDetailsResponse['ride'] = await response.json();

  return data;
};

const fetchInviteShare = async (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  inviteId: string,
) => {
  const baseURL = getInviteFriendsBaseUrl(request);
  const headers = {
    'Peloton-Platform': 'web',
    'User-Agent': 'peloton/share-proxy',
  };
  const url = new URL(`/public/invite_share/${inviteId}`, baseURL).toString();
  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.json();

  return data;
};

export const fetchClassData = async (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  classId: string,
): Promise<RideDetailsResponse> => {
  try {
    const rideDetails = await fetchRideDetails(request, query, classId);

    return { ride: rideDetails };
  } catch (err) {
    console.error(err);
    return {} as RideDetailsResponse;
  }
};

export type DetailedWorkoutResponse = {
  workout: {
    share_title: string;
    share_description: string;
  };
};

export const fetchDetailedWorkoutText = async (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  shareWorkoutId: string,
) => {
  const { baseURL, headers } = getFetchOptions(request, query);
  const url = new URL(`/api/share/workout/text/${shareWorkoutId}`, baseURL).toString();

  const response = await fetch(url, { headers });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const detailedWorkoutResponse: DetailedWorkoutResponse['workout'] = await response.json();

  return detailedWorkoutResponse;
};

export const fetchDetailedWorkoutData = async (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  shareWorkoutId: string,
): Promise<DetailedWorkoutResponse> => {
  try {
    const detailedWorkoutText = await fetchDetailedWorkoutText(
      request,
      query,
      shareWorkoutId,
    );

    return { workout: detailedWorkoutText };
  } catch (err) {
    console.error(err);
    return {} as DetailedWorkoutResponse;
  }
};

export const fetchInviteDetails = async (
  request: IncomingMessage,
  query: ParsedUrlQuery,
  inviteId: string,
): Promise<InviteDetailsResponse> => {
  try {
    const inviteResponseData = await fetchInviteShare(request, query, inviteId);
    const inviteShareRes = camelizeKeys(inviteResponseData) as InviteShareResult;
    const deserializedScheduleInfo = camelizeKeys(
      JSON.parse(Buffer.from(inviteShareRes.scheduledClassId, 'base64').toString()),
    ) as DeserializedScheduleInfo;

    const rideDetails = await fetchRideDetails(
      request,
      query,
      deserializedScheduleInfo.rideId,
    );

    return {
      rideDetails,
      deserializedScheduleInfo,
      inviteShareRes,
    };
  } catch (err) {
    console.error(err);
    return {} as InviteDetailsResponse;
  }
};
